import React, { useState, useEffect, useRef } from "react";
import { Container } from "reactstrap";
import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/css";
import "swiper/css/navigation";
import Header from "../../PublicOrdering/pages/Header";
import TouchBasedHeader from "../../PublicOrdering/TourchBased/TouchBasedHeader";
import ThemeFooter from "../component/ThemeFooter";
import AdLogService from "../../../services/AdService/AdLogService";
import AdLogQueueService from "../../../services/AdService/AdLogQueueService";

/**
 * PublicOrderingLayout Component
 * Provides consistent layout structure for all public ordering pages
 *
 * Props:
 * - children: Content to display in the middle section
 * - orgData: Organization data for headers
 * - orgcode: Organization code
 * - ads: Array of ads to display (optional, if not provided ads will be fetched)
 * - deviceInfo: Device info (optional, will be fetched from localStorage if not provided)
 * - isTouchBasedSetting: Boolean to determine header type
 * - setIsTouchBasedSetting: Callback to update touch-based setting
 */
const PublicOrderingLayout = ({
  children,
  orgData,
  orgcode,
  ads: initialAds = [],
  deviceInfo: initialDeviceInfo = null,
  isTouchBasedSetting = true,
  isVideoPlaying,
  setIsTouchBasedSetting = () => {},
}) => {
  const appType = window?.APP_CONFIG?.appType;
  const isTouchBased = appType === "TouchBased";

  // State management
  const [ads, setAds] = useState(initialAds);
  const [deviceInfo, setDeviceInfo] = useState(initialDeviceInfo);
  // const [isVideoPlaying, setIsVideoPlaying] = useState(false);

  // Refs
  const adsFetchedRef = useRef(false);
  const swiperRef = useRef(null);
  const slideTimerRef = useRef(null);

  const currentAdRef = useRef(null);

  const isVideoPlayingRef = useRef(isVideoPlaying);

  useEffect(() => {
    isVideoPlayingRef.current = isVideoPlaying;
  }, [isVideoPlaying]);

  const isAdsDifferent = (prevAds, newAds) => {
    if (prevAds.length !== newAds.length) return true;

    const prevMap = new Map(prevAds.map((ad) => [ad.id, ad]));

    for (let ad of newAds) {
      const prev = prevMap.get(ad.id);
      if (!prev) return true;

      if (
        prev.priority !== ad.priority ||
        prev.mediaPath !== ad.mediaPath ||
        prev.durationSeconds !== ad.durationSeconds ||
        prev.videoLengthSeconds !== ad.videoLengthSeconds ||
        prev.mediaType !== ad.mediaType ||
        prev.adFormatValue !== ad.adFormatValue
      ) {
        return true;
      }
    }

    return false;
  };
  // Get device info from localStorage if not provided
  useEffect(() => {
    if (initialDeviceInfo) {
      setDeviceInfo(initialDeviceInfo);
      return;
    }

    try {
      const deviceContext = localStorage.getItem("deviceContext");
      const macId = localStorage.getItem("macId");
      const ipAddress = localStorage.getItem("ipAddress") || "0.0.0.0";

      if (deviceContext) {
        const context = JSON.parse(deviceContext);
        setDeviceInfo({
          macId: macId,
          deviceName: context.deviceName || "Unknown Device",
          ipAddress: ipAddress,
          deviceCode: context.deviceCode,
          orgCode: context.orgCode,
          terminal: context.terminal || "T1",
        });
      }
    } catch (error) {
      console.error("Error reading device info:", error);
    }
  }, [initialDeviceInfo]);

  // Initialize AdLogQueueService with duration from API
  useEffect(() => {
    const macId = localStorage.getItem("macId");
    if (macId) {
      AdLogQueueService.startDurationRefresh(macId);
    }

    // Cleanup on unmount
    return () => {
      AdLogQueueService.stopDurationRefresh();
    };
  }, []);

  useEffect(() => {
    if (!isVideoPlayingRef.current) {
      const swiper = swiperRef.current?.swiper;
      if (!swiper || bannerAds.length === 0) return;

      // restart timer for current slide
      const currentIndex = swiper.realIndex;
      handleSlideChange(currentIndex);
    } else {
      // If video starts, stop timer
      if (slideTimerRef.current) {
        clearTimeout(slideTimerRef.current);
      }
    }
  }, [isVideoPlaying]);
  // Log ad impression when banner is displayed
  const logAdImpression = (ad, visibleSeconds) => {
    if (!deviceInfo) return;

    const duration = visibleSeconds || ad.durationSeconds || 5;

    const now = new Date();
    const adLog = {
      adId: ad.id,
      macId: deviceInfo.macId,
      deviceName: deviceInfo.deviceName,
      ipAddress: deviceInfo.ipAddress,
      shownAt: now.toISOString(),
      hiddenAt: new Date(now.getTime() + duration * 1000).toISOString(),
      visibleSeconds: duration,
      playedSeconds: duration,
      wasCompleted: true,
      wasSkipped: false,
      displayReason: 1,
      screenName: "Banner",
      appVersion: "v1",
    };
    AdLogQueueService.enqueue(adLog);

    console.log(
      `📝 Queued Ad Impression: ID=${ad.id}, Duration=${duration}s`,
      adLog,
    );
  };

  // Handle banner slide (images and videos)
  const handleBannerSlide = (swiper) => {
    if (!swiper) return;

    const index = swiper.realIndex;
    const ad = bannerAds[index];
    if (!ad) return;

    currentAdRef.current = ad;

    // Clear previous timer
    if (slideTimerRef.current) clearTimeout(slideTimerRef.current);

    const mediaType = Number(ad.mediaType);

    // Helper function to safely advance slide
    const safeSlideNext = () => {
      const currentSwiper = swiperRef.current?.swiper;
      if (currentSwiper && typeof currentSwiper.slideNext === 'function') {
        // Only slide next if there are multiple slides
        if (bannerAds.length > 1) {
          currentSwiper.slideNext();
        } else {
          console.log("🔄 Single slide detected, restarting");
        }
      } else {
        console.warn("⚠️ Swiper instance not available for slideNext");
      }
    };

    // 🖼️ IMAGE BANNER
    if (mediaType === 1 && !isVideoPlayingRef.current) {
      const duration = ad.durationSeconds || 5;
      console.log("🖼️ Image banner displayed:", ad.id, "Duration:", duration);
      slideTimerRef.current = setTimeout(() => {
        if (!isVideoPlayingRef.current) {
          logAdImpression(ad, duration);
          safeSlideNext();
        }
      }, duration * 1000);
    }

    // 🎬 VIDEO BANNER
    if (mediaType === 2 && !isVideoPlayingRef.current) {
      const videoEl = swiper.slides[swiper.activeIndex]?.querySelector("video");
      console.log("🎬 Banner Video Element Found:", !!videoEl, "Ad ID:", ad.id);
      
      if (videoEl) {
        videoEl.currentTime = 0;
        console.log("▶️ Attempting to play banner video:", ad.id);
        
        videoEl
          .play()
          .then(() => {
            console.log("✅ Banner video playing successfully:", ad.id);
          })
          .catch((err) => {
            console.error("❌ Video play failed:", err, ad);
          });

        const duration = ad.videoLengthSeconds || ad.durationSeconds || 10;

        // Clear previous timer if exists
        if (slideTimerRef.current) clearTimeout(slideTimerRef.current);

        // Fallback timer in case video doesn't fire onended
        slideTimerRef.current = setTimeout(() => {
          console.warn("⏰ Fallback timer fired for video:", ad.id);
          logAdImpression(ad, duration);
          safeSlideNext();
        }, (duration + 0.5) * 1000); // slight buffer

        // onended handler
        videoEl.onended = () => {
          console.log("🏁 Video ended:", ad.id);
          clearTimeout(slideTimerRef.current); // stop fallback
          logAdImpression(ad, duration);
          
          // For single video, replay it
          if (bannerAds.length === 1) {
            console.log("🔁 Single banner video - replaying");
            videoEl.currentTime = 0;
            videoEl.play().catch(err => console.warn("Replay failed:", err));
            
            // Log impression and restart timer
            slideTimerRef.current = setTimeout(() => {
              logAdImpression(ad, duration);
              videoEl.onended();
            }, duration * 1000);
          } else {
            safeSlideNext();
          }
        };
      } else {
        console.error("❌ Banner video element not found for ad:", ad.id);
      }
    } else if (mediaType === 2 && isVideoPlayingRef.current) {
      console.log("⏸️ Skipping banner video due to fullscreen video playing:", {
        adId: ad.id
      });
    }
  };

  const handleSlideChange = (index) => {
    if (!bannerAds.length || !deviceInfo) return;

    // Clear previous timer
    if (slideTimerRef.current) {
      clearTimeout(slideTimerRef.current);
    }

    const ad = bannerAds[index];
    if (!ad) return;

    currentAdRef.current = ad;

    const mediaType = Number(ad.mediaType);

    // 🖼 IMAGE AD FLOW
    if (mediaType === 1) {
      const duration = ad.durationSeconds || 5;

      slideTimerRef.current = setTimeout(async () => {
        logAdImpression(ad, duration);
        swiperRef.current?.swiper?.slideNext();
      }, duration * 1000);
    }

    // 🎬 VIDEO AD FLOW
    if (mediaType === 2) {
      // Do nothing here.
      // Video will control logging onEnded
    }
  };

  // Fetch ads on component mount
  useEffect(() => {
    if (adsFetchedRef.current || !deviceInfo) {
      return;
    }

    const fetchAds = async () => {
      try {
        const data = await AdLogService.getAdsByMacAddress(deviceInfo.macId);
        const baseApi =
          window.APP_CONFIG?.api4?.baseapi ||
          "https://api-databasewebservice.danfesolution.com";

        const resolveMediaUrl = (path) => {
          if (!path) return "";
          if (/^https?:\/\//i.test(path)) return path;
          if (path.startsWith("/")) return `${baseApi}${path}`;
          return `${baseApi}/${path}`;
        };

        const normalizedAds = (Array.isArray(data) ? data : [])
          .sort((a, b) => a.priority - b.priority)
          .map((ad) => ({
            id: ad.id,
            title: ad.title,
            description: ad.description,
            mediaType: Number(ad.mediaType ?? ad.mediaTypeValue ?? 0),
            mediaTypeValue: Number(ad.mediaTypeValue ?? ad.mediaType ?? 0),
            adFormat: Number(ad.adFormat ?? ad.adFormatValue ?? 0),
            adFormatValue: Number(ad.adFormatValue ?? ad.adFormat ?? 0),
            mediaPath: resolveMediaUrl(ad.mediaPath),
            thumbnailPath: resolveMediaUrl(ad.thumbnailPath),
            priority: ad.priority ?? 999,
            durationSeconds: ad.durationSeconds || 10,
            videoLengthSeconds: ad.videoLengthSeconds || 0,
          }));
        setAds((prevAds) => {
          if (!isAdsDifferent(prevAds, normalizedAds)) {
            return prevAds;
          }

          return normalizedAds;
        });
      } catch (error) {
        console.error("Error fetching ads by MAC address:", error);
      }
    };

    adsFetchedRef.current = true;
    fetchAds();

    // Polling to refresh ads every 30 seconds
    const adRefreshInterval = setInterval(fetchAds, 30000);
    return () => clearInterval(adRefreshInterval);
  }, [deviceInfo]);

  // Slideshow effect for image and video ads (Banner or FullScreen format)
  // const bannerAds = ads.filter((ad) => {
  //   const adFormat = Number(ad.adFormatValue ?? ad.adFormat ?? 0);
  //   const mediaType = Number(ad.mediaTypeValue ?? ad.mediaType ?? 0);
  //   return (
  //     (adFormat === 1 || adFormat === 2) && (mediaType === 1 || mediaType === 2)
  //   ); // adFormat: 1 = FullScreen, 2 = Banner, mediaType: 1 = image, 2 = video
  // });

  // Banner Ads only: Images + Banner Videos
  const bannerAds = ads.filter((ad) => {
    const mediaType = Number(ad.mediaTypeValue ?? ad.mediaType ?? 0);
    const adFormat = Number(ad.adFormatValue ?? ad.adFormat ?? 0);

    return (
      // Images always included
      mediaType === 1 ||
      // Videos included only if adFormat = Banner (2)
      (mediaType === 2 && adFormat === 2)
    );
  });

  // Note: handleSlideChange is kept for backward compatibility but 
  // the main banner slide handling is now done by handleBannerSlide
  useEffect(() => {
    if (!deviceInfo || bannerAds.length === 0) return;

    const swiper = swiperRef.current?.swiper;
    if (!swiper) return;

    // Manually trigger the first slide when ads change or component loads
    console.log("🔄 Banner ads updated, initializing slide");
    handleBannerSlide(swiper);
  }, [
    deviceInfo,
    bannerAds
      .map(
        (ad) =>
          `${ad.id}-${ad.mediaPath}-${ad.durationSeconds}-${ad.videoLengthSeconds}-${ad.priority}`,
      )
      .join("|"),
  ]);
  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (slideTimerRef.current) {
        clearTimeout(slideTimerRef.current);
      }
    };
  }, []);

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        minHeight: "100vh",
        gap: "8px",
        touchAction: "none",
        overscrollBehavior: "contain",
        overscrollBehaviorY: "contain",
      }}
    >
      {/* HEADER - Top */}
      <div
        className="sticky-top bg-white z-10"
        style={{
          boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
          touchAction: "none",
          overscrollBehavior: "contain",
          overscrollBehaviorY: "contain",
        }}
      >
        {isTouchBased ? (
          <TouchBasedHeader
            orgData={orgData}
            orgcode={orgcode}
            setIsTouchBasedSetting={setIsTouchBasedSetting}
            isTouchBasedSetting={isTouchBasedSetting}
          />
        ) : (
          <Header orgData={orgData} orgcode={orgcode} />
        )}
      </div>

      {/* CONTENT - Middle (Flexible) */}
      <div
        style={{
          flex: 1,
          overflowY: "auto",
          display: "flex",
          flexDirection: "column",
          overflowX: "hidden",
        }}
      >
        <Container fluid style={{ maxWidth: "1600px" }} className="p-3">
          {children}
        </Container>
      </div>

      {/* AD SPACE - Sticky Bottom */}
      {isTouchBased && (
        <div className="position-fixed bottom-0 w-100 ">
          <Container fluid style={{ maxWidth: "1600px" }} className="px-3 mb-3">
            {bannerAds.length > 0 && (
              <div>
                {/* <Swiper
                  loop={true}
                  speed={800}
                  allowTouchMove={false}
                  simulateTouch={false}
                  ref={swiperRef}
                  onSlideChange={(swiper) => {
                    handleSlideChange(swiper.realIndex);
                  }}
                  style={{
                    borderRadius: "12px",
                    overflow: "hidden",
                    maxHeight: "50vh",
                  }}
                >
                  {bannerAds.map((ad) => (
                    <SwiperSlide key={ad.id}>
                      {Number(ad.mediaType) === 1 ? (
                        // Image Banner
                        <img
                          src={ad.mediaPath}
                          alt={ad.title}
                          style={{
                            width: "100%",
                            height: "auto",
                            display: "block",
                          }}
                        />
                      ) : Number(ad.mediaType) === 2 ? (
                        // Video Banner
                        <video
                          ref={(el) => (ad.videoEl = el)} // store element on ad object
                          style={{
                            width: "100%",
                            height: "auto",
                            display: "block",
                          }}
                          autoPlay
                          muted
                          playsInline
                          onTimeUpdate={(e) => {
                            const video = e.target;
                            const duration =
                              ad.videoLengthSeconds || ad.durationSeconds || 10;

                            if (video.currentTime >= duration) {
                              logAdImpression(ad, duration);
                              swiperRef.current?.swiper?.slideNext();
                            }
                          }}
                        >
                          <source src={ad.mediaPath} type="video/mp4" />
                        </video>
                      ) : null}
                    </SwiperSlide>
                  ))}
                </Swiper> */}
                <Swiper
                  loop={bannerAds.length > 1}
                  speed={800}
                  allowTouchMove={false}
                  simulateTouch={false}
                  ref={swiperRef}
                  onSwiper={(swiper) => {
                    // Initialize first slide on swiper load
                    console.log("🎯 Swiper initialized, triggering first slide");
                    setTimeout(() => {
                      handleBannerSlide(swiper);
                    }, 100);
                  }}
                  onSlideChangeTransitionEnd={() => {
                    const swiper = swiperRef.current?.swiper;
                    if (!swiper) return;
                    handleBannerSlide(swiper);
                  }}
                  style={{
                    borderRadius: "12px",
                    overflow: "hidden",
                    maxHeight: "50vh",
                  }}
                >
                  {bannerAds.map((ad) => (
                    <SwiperSlide key={ad.id}>
                      {Number(ad.mediaType) === 1 ? (
                        <img
                          src={ad.mediaPath}
                          alt={ad.title}
                          style={{
                            width: "100%",
                            height: "auto",
                            display: "block",
                          }}
                        />
                      ) : (
                        <video
                          muted
                          playsInline
                          preload="auto"
                          controlsList="nodownload nofullscreen noremoteplayback"
                          disablePictureInPicture
                          style={{
                            width: "100%",
                            height: "auto",
                            display: "block",
                          }}
                          onLoadedData={(e) => {
                            console.log("Video loaded:", ad.id);
                          }}
                        >
                          <source src={ad.mediaPath} type="video/mp4" />
                        </video>
                      )}
                    </SwiperSlide>
                  ))}
                </Swiper>
              </div>
            )}
          </Container>
          <ThemeFooter />
        </div>
      )}

      {/* FOOTER - Bottom */}
      {/* <div>{isTouchBased && <ThemeFooter />}</div> */}
    </div>
  );
};

export default PublicOrderingLayout;
